import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { Tooltip } from "antd";
import { useAppSelector } from "../../../..";
import { CommonChildTableProps, TableColumn } from "../../../../lib/interfaces";

import {
  checkIsLoading,
  getCommonChildTableProps,
  getDictionaryItemById,
  toDisplayFormat,
} from "../../../../lib/utils";
import ToggleableVersionsTable from "../../../../components/ToggleableVersionsTable/ToggleableVersionsTable";
import { combineClasses } from "../../../../lib/utils/combineClasses";
import { AggregationRule } from "../../../../lib/types/AggregationRule";
import { Offer } from "../../../../lib/types";
import { IRuleCondition } from "../../../../lib/types/IRuleCondition";
import { parseRuleCondition } from "../../../../containers/OfferContainer/utils/parseRuleCondition";
import { RuleConditionView } from "./RuleConditionView/RuleConditionView";
import { ReactComponent as DotsIcon } from "../../../../img/3dots.svg";
import { ReactComponent as TransitionIcon } from "../../../../img/transition_icon.svg";
import { ReactComponent as CopyIcon } from "../../../../img/copy-icon.svg";
import "./AggregationRulesViewTable.less";

type TableDataRecord = Pick<
  AggregationRule<"view">,
  "id" | "aggName" | "periodTypeId" | "rangeFrom" | "rangeTo" | "offeringId" | "fromDate"
> & { ruleConditions: IRuleCondition[]; period: string } & CommonChildTableProps;

type FieldName = keyof TableDataRecord | "btns" | "actions";

type ColumnWidths = Partial<Record<FieldName, string>>;

const columnWidths: ColumnWidths = {
  // aggName: "15%",
  periodTypeId: "12%",
  rangeFrom: "9%",
  rangeTo: "9%",
  ruleConditions: "33%",
  actions: "62px",
  btns: "48px",
  period: "180px",
};

const ruleConditionColumnWidths: Record<
  "includeList" | "documentTypeList" | "direction",
  string
> = {
  includeList: "33%",
  documentTypeList: "33%",
  direction: "34%",
};

interface ComponentProps {
  className?: string;
  aggregationRules: Offer<string, "view">["aggregationRules"];
  isToggleableColumns: boolean;
  aggregationRuleVersions?: Offer<string, "view">["aggregationRules"];
  isVersionsLoading?: boolean;
  showHistoryModal?: (ruleId: number) => void;
  onOpenVersion?: (ruleId: number, isVersionsOpen: boolean) => void;
  isLoading?: boolean;
  priceCreationAllowed?: boolean;
  displayStyleAsChild?: true; // Для целей отображения стилей верхней записи, как вложенной
  isProductDetails?: boolean;
}

/* ------------------------------------------------------ Начало компонента ------------------------------------------------------ */

export const AggregationRulesViewTable = (props: ComponentProps) => {
  const {
    className,
    aggregationRules,
    isLoading,
    onOpenVersion,
    showHistoryModal,
    aggregationRuleVersions,
    isVersionsLoading,
    isToggleableColumns,
    priceCreationAllowed,
    displayStyleAsChild,
    isProductDetails,
  } = props;
  const history = useHistory();

  const location = useLocation();
  const offerFromDate = new URLSearchParams(location.search).get("onDate")?.replace(" ", "+");

  const { periodTypes, periodTypesLoading } = useAppSelector((state) => state.rtkDictionaries);

  const rowClassName = () => {
    if (displayStyleAsChild) {
      return "row_child";
    }
    return "";
  };

  const childRowClassName = (record: TableDataRecord) => {
    if (record.isCurrentVersion) {
      return `row_child row_current-version`;
    }
    if (record.isPastVersion) {
      return "row_child row_past-version";
    }
    return "row_child";
  };

  const ruleConditionRowClassName = useCallback(
    (record: TableDataRecord) => {
      if (displayStyleAsChild) {
        return "row_child";
      }
      if (record.isChild) {
        return childRowClassName(record);
      }
      return "";
    },
    [displayStyleAsChild]
  );

  const getColumns = useCallback((): TableColumn<TableDataRecord>[] => {
    const ruleConditionsTitle = () => (
      <div className="aggregationRules-viewTable__ruleConditionsTitle">
        {/* <div style={ruleConditionColumnsStyle.btnSpace}> </div> */}
        <div style={{ width: ruleConditionColumnWidths.includeList }}>Контрагент</div>
        <div style={{ width: ruleConditionColumnWidths.documentTypeList }}>Тип</div>
        <div style={{ width: ruleConditionColumnWidths.direction }}>Направление</div>
      </div>
    );
    ruleConditionsTitle.toString = () => "Правила";
    return [
      {
        title: "Название группы",
        key: "aggName",
        dataIndex: "aggName",
        width: columnWidths.aggName,
      },
      {
        title: "Период накопления",
        key: "periodTypeId",
        dataIndex: "periodTypeId",
        width: columnWidths.periodTypeId,
        render: (periodTypeId) => getDictionaryItemById(periodTypes, periodTypeId)?.name,
      },
      {
        title: "От (шт)",
        key: "rangeFrom",
        dataIndex: "rangeFrom",
        width: columnWidths.rangeFrom,
      },
      {
        title: "До (шт)",
        key: "rangeTo",
        dataIndex: "rangeTo",
        width: columnWidths.rangeTo,
      },
      {
        title: ruleConditionsTitle,
        key: "ruleConditions",
        dataIndex: "ruleConditions",
        width: columnWidths.ruleConditions,
        render: (v, record) => {
          return (
            <RuleConditionView
              ruleConditions={record.ruleConditions}
              ruleConditionColumnWidths={ruleConditionColumnWidths}
              rowClassNameString={ruleConditionRowClassName(record)}
            />
          );
        },
      },
      {
        title: "Период",
        key: "period",
        dataIndex: "period",
        width: columnWidths.period,
      },
      {
        key: "actions",
        width: columnWidths.actions,
        render: (x, { id, offeringId, fromDate }) => {
          return (
            <>
              {priceCreationAllowed && !isProductDetails && (
                <Tooltip title="Скопировать группу трафика">
                  <CopyIcon
                    className="aggregationRules-viewTable__copyButton"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (offerFromDate) {
                        const path = `/offer/${offeringId}/aggregation-rule/create/${id}?onDate=${offerFromDate}&onDateRule=${fromDate}`;
                        history.push(path);
                      }
                    }}
                  />
                </Tooltip>
              )}
              <Tooltip title="Перейти к группе трафика">
                <TransitionIcon
                  className="aggregationRules-viewTable__priceBtn"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (offerFromDate) {
                      const path = `/offer/${offeringId}/aggregation-rule/${id}?onDate=${offerFromDate}&onDateRule=${fromDate}`;
                      history.push(path, { isProductDetails });
                    }
                  }}
                />
              </Tooltip>
            </>
          );
        },
      },
      {
        key: "btns",
        width: columnWidths.btns,
        render: (x, { id }) => {
          return showHistoryModal ? (
            <DotsIcon
              className="aggregationRules-viewTable__history"
              onClick={(e) => {
                e.stopPropagation();
                showHistoryModal(id);
              }}
            />
          ) : null;
        },
      },
    ];
  }, [
    history,
    isProductDetails,
    offerFromDate,
    periodTypes,
    priceCreationAllowed,
    ruleConditionRowClassName,
    showHistoryModal,
  ]);

  const [columns, setColumns] = useState(getColumns());

  // Актуализация отображения колонок
  useEffect(() => {
    // const forbiddenKeys: Record<number, FieldName[]> = {
    //   1: ["rangeFrom", "rangeTo", "ratePeriodId"],
    //   2: ["rangeFrom", "rangeTo", "ratePeriodId"],
    //   3: [],
    // };
    const newColumns = getColumns();
    setColumns((prev) => {
      return prev.map((prevColumn) => {
        const newColumn = newColumns.find(({ key }) => key === prevColumn.key);
        return {
          ...prevColumn,
          title: newColumn?.title ? newColumn.title : prevColumn?.title,
          render: newColumn?.render ? newColumn.render : prevColumn?.render,
        };
      });
    });
  }, [getColumns, periodTypes]);

  const tableData = useMemo<TableDataRecord[]>(() => {
    if (aggregationRules) {
      return [...aggregationRules]
        .sort((a, b) => (a.id > b.id ? 1 : -1))
        .map((rule, i) => {
          const ruleConditions = parseRuleCondition(rule.ruleCondition);
          const fromDate = toDisplayFormat(rule.fromDate);
          const toDate = toDisplayFormat(rule.toDate);
          const rec: TableDataRecord = {
            ...rule,
            ...getCommonChildTableProps(rule, i, aggregationRules.length),
            ruleConditions,
            period: `${fromDate} - ${toDate}`,
          };
          return rec;
        });
    }
    return [];
  }, [aggregationRules]);

  const childTableData = useMemo((): TableDataRecord[] => {
    return (
      aggregationRuleVersions?.map((rule, i) => ({
        ...rule,
        ruleConditions: parseRuleCondition(rule.ruleCondition),
        ...getCommonChildTableProps(rule, i, aggregationRuleVersions?.length, true),
        period: `${rule.fromDate ? toDisplayFormat(rule.fromDate) : ""} - ${
          rule.toDate ? toDisplayFormat(rule.toDate) : ""
        }`,
      })) || []
    );
  }, [aggregationRuleVersions]);

  return (
    <div className={combineClasses("aggregationRules-viewTable", className)}>
      <ToggleableVersionsTable
        columns={columns.filter(({ key }) => key !== "actions")}
        childTableColumns={columns}
        setColumns={setColumns}
        tableData={tableData}
        isTableLoading={checkIsLoading(periodTypesLoading) || isLoading}
        rowClassName={rowClassName}
        childRowClassName={childRowClassName}
        isChildTableLoading={isVersionsLoading}
        childTableData={childTableData}
        onOpenVersionsCallback={({ id }, isVersionsOpen) => onOpenVersion?.(id, isVersionsOpen)}
        onCloseVersionsCallback={({ id }, isVersionsOpen) => onOpenVersion?.(id, isVersionsOpen)}
        isVisibleEye={!!onOpenVersion}
        isToggleableColumns={isToggleableColumns}
        style={{ marginTop: "0" }}
        tableName="aggregationRules-viewTable"
      />
    </div>
  );
};
